import type { HelpArticle } from '@didaskill/domain/help-articles/types';
import type {
  PatchHelpArticlesPayload,
  PostHelpArticlesPayload,
  PublishHelpArticlePayload,
} from '@didaskill/domain/news/types';
import type { TypedResponse } from '@didaskill/types/misc';

import { HttpRepository } from '../httpRepository';

const BASE_HELP_ARTICLES_URI = '/api/v1/news/help-articles';

export class HelpArticlesRepository extends HttpRepository {
  /**
   * Fetch help articles list for backoffice users.
   *
   * @returns Array of help articles.
   */
  async getHelpArticlesList(): Promise<HelpArticle[]> {
    const { body } = await this.http.get(BASE_HELP_ARTICLES_URI);

    return body;
  }

  /**
   * Create help article with a title.
   *
   * @param params - Params.
   * @param params.title - Title of the article.
   * @returns Created help article.
   */
  async createHelpArticle(params: PostHelpArticlesPayload): Promise<TypedResponse<HelpArticle>> {
    const body = await this.http.post(BASE_HELP_ARTICLES_URI).send(params);

    return body;
  }

  /**
   * Update help article with a draft.
   *
   * @param helpArticleId - Id of the help article.
   * @param params - Params.
   * @param params.draft - Draft of the article.
   * @returns Updated help article.
   */
  async updateHelpArticle(
    helpArticleId: string,
    params: PatchHelpArticlesPayload,
  ): Promise<TypedResponse<HelpArticle>> {
    const body = await this.http.patch(`${BASE_HELP_ARTICLES_URI}/${helpArticleId}`).send(params);

    return body;
  }

  /**
   * Publish help article.
   *
   * @param helpArticleId - Id of the help article.
   * @param params - Params.
   * @param params.draft - Draft of the article.
   * @returns Updated help article.
   */
  async publishHelpArticle(
    helpArticleId: string,
    params: PublishHelpArticlePayload,
  ): Promise<TypedResponse<HelpArticle>> {
    const body = await this.http
      .post(`${BASE_HELP_ARTICLES_URI}/${helpArticleId}/publish`)
      .send(params);

    return body;
  }

  /**
   * Delete help article.
   *
   * @param helpArticleId - Id of the help article.
   * @returns True if help article is deleted.
   */
  async deleteHelpArticle(helpArticleId: string) {
    await this.http.delete(`${BASE_HELP_ARTICLES_URI}/${helpArticleId}`);
  }

  /**
   * Increment help article views.
   *
   * @param helpArticleId - Id of the help article.
   * @returns Updated help article views.
   */
  async incrementHelpArticleViews(
    helpArticleId: string,
  ): Promise<TypedResponse<{ views: number }>> {
    const body = await this.http.post(`${BASE_HELP_ARTICLES_URI}/${helpArticleId}/views`);

    return body;
  }
}
